import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["sort", "query"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { PAGINATED_APP_GRID_CONFIG, PaginatedAppGridWithAtlassianApps } from '@atlassian/mpac-app-tile';
import { NoResultView, useMarketplaceCrossFlow } from '@atlassian/mpac-primitives';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import React, { useCallback, useState } from 'react';
import { useAtlassianAppsAnalytics } from '../hooks/useAtlassianAppsAnalytics';
import useGetUnlicensedAtlassianProducts from '../hooks/useGetUnlicensedAtlassianProducts';
import { useSearchPageAnalytics } from '../hooks/useSearchPageAnalytics';
import { EndPageLabel } from '.';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function
export function SearchResultsWithAtlassianApps(_ref) {
  let {
    searchInputRef,
    gridProps,
    isSearchQueryActive,
    areFiltersActive,
    handleClearAllFilters,
    analyticsFilters,
    selectedHostingFilter,
    cloudId
  } = _ref;
  const {
    totalCount,
    appList,
    isLoading,
    isLoadingMore,
    showLoadMoreButton,
    fetchMoreApps,
    initialFetchError,
    fetchMoreError,
    rawData
  } = gridProps;
  const {
    unlicensedAtlassianProducts,
    loading: loadingUnlicensedAtlassianProducts
  } = useGetUnlicensedAtlassianProducts(cloudId);
  const showInitialLoading = loadingUnlicensedAtlassianProducts || isLoading || !rawData && !initialFetchError;
  const hasResults = !showInitialLoading && !initialFetchError;
  const showEndReachedLabel = hasResults && totalCount > 0;
  const showNoResultView = hasResults && totalCount === 0;
  const [tileToFocusIndex, setTileToFocusIndex] = useState(-1);
  const source = useSource();
  const environment = useEnvironment();
  const {
    crossFlow
  } = useMarketplaceCrossFlow();
  const {
    handleAtlassianAppsViewedEvent,
    handleAtlassianAppsInteractedEvent
  } = useAtlassianAppsAnalytics(unlicensedAtlassianProducts);
  const onAppClick = (app, cardPosition) => {
    crossFlow({
      onCrossFlow: () => {
        handleAtlassianAppsInteractedEvent(app.targetProduct, cardPosition);
      },
      targetProduct: app.targetProduct,
      sourceComponent: 'atlassianAppsTileSection',
      sourceContext: 'atlassianAppTile'
    });
  };
  const atlassianAppList = unlicensedAtlassianProducts.map((app, index) => _objectSpread(_objectSpread({}, app), {}, {
    onClick: () => onAppClick(app, index)
  }));
  const {
    handleTileClickAnalytics,
    handleLoadMoreClickAnalytics,
    handleClearAllFiltersAnalytics,
    handleYourSearchQueryClickAnalytics
  } = useSearchPageAnalytics();
  const handleOnTileClick = (tileData, index) => {
    handleTileClickAnalytics({
      tileData,
      hitRank: index + 1,
      filters: analyticsFilters
    });
  };
  const handleLoadMoreClick = async event => {
    handleLoadMoreClickAnalytics({
      pageNumber: appList.length / PAGINATED_APP_GRID_CONFIG.fetchMoreCount,
      filters: analyticsFilters
    });
    await fetchMoreApps(event);

    // if load more is triggered via keypress, focus first newly loaded tile
    if (event.detail === 0) setTileToFocusIndex(appList.length);
  };
  const onClearFiltersClick = () => {
    handleClearAllFilters === null || handleClearAllFilters === void 0 ? void 0 : handleClearAllFilters();
    const {
        sort,
        query
      } = analyticsFilters,
      filters = _objectWithoutProperties(analyticsFilters, _excluded);
    handleClearAllFiltersAnalytics({
      filters
    });
  };
  const handleLinkClick = () => {
    var _searchInputRef$curre, _searchInputRef$curre2;
    (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 ? void 0 : _searchInputRef$curre.focus();
    handleYourSearchQueryClickAnalytics({
      query: (_searchInputRef$curre2 = searchInputRef.current) === null || _searchInputRef$curre2 === void 0 ? void 0 : _searchInputRef$curre2.value
    });
  };
  const handleTileToFocus = useCallback(ref => {
    if (ref) {
      ref.focus();
      setTileToFocusIndex(-1);
    }
  }, []);
  if (showNoResultView) {
    return /*#__PURE__*/_jsx(NoResultView, {
      showClearFilterButton: areFiltersActive,
      onClearFiltersClick: onClearFiltersClick,
      title: "We couldn't find a match for your query!",
      description: areFiltersActive ? 'Try rephrasing or changing your selection of filters' : 'Try rephrasing or search with another query'
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PaginatedAppGridWithAtlassianApps, {
      isLoading: showInitialLoading,
      isLoadingMore: isLoadingMore,
      onLoadMoreClick: handleLoadMoreClick,
      showLoadMoreButton: showLoadMoreButton,
      fetchMoreError: fetchMoreError,
      initialFetchError: initialFetchError,
      isSearchQueryActive: isSearchQueryActive,
      areFiltersActive: areFiltersActive,
      appList: appList,
      environment: environment,
      source: source,
      onTileClick: handleOnTileClick,
      tileToFocusIndex: tileToFocusIndex,
      handleTileToFocus: handleTileToFocus,
      selectedHostingFilter: selectedHostingFilter,
      atlassianAppList: atlassianAppList,
      onAtlassianAppSectionViewed: handleAtlassianAppsViewedEvent
    }), showEndReachedLabel && /*#__PURE__*/_jsx(EndPageLabel, {
      onLinkClick: handleLinkClick
    })]
  });
}